import {
  ATTEND_STATUS_META,
  GROUP_BAR_COLOR,
  EVENT_TYPE_META,
  GROUP_STATUS_CARD_META,
  CONSULT_RESULT_CARD_META,
} from './PrivateLessonStyled';
import { getEventTypeKey } from './hooks/usePrivateLessonData';
import { getConsultResultLabel } from '../../../constants/consultation';

// 개인/듀엣 레슨 출석 상태 파생·집계 유틸 (스케줄 보기 리뉴얼)
// - 백엔드 예약 상태 카운트(attended/absented/reservedMemberCount) 기반으로 파생한다.
// - 취소된 예약은 캘린더에 표시하지 않는 정책이라 별도 상태가 없다 (카운트 전부 0 = 예약 취급).
// - 지난 수업인데 아직 예약 상태인 카드도 예약색 그대로 둔다 (자동결석 배치가 6시간 주기로 결석 전환).

export const isLessonEvent = event =>
  event.courseClassType === 'PRIVATE_LESSON' ||
  event.courseClassType === 'DUET_LESSON' ||
  event.courseClassType === 'OT';

// 여러 명 OT(정원 2명 이상) — 그룹레슨처럼 카드 한 장·그룹 참석관리 모달을 쓴다
export const isOtGroupEvent = event => event?.courseClassType === 'OT' && (Number(event.membersMaxCount) || 0) > 1;

// OT 수강권 드롭다운 값 — 수강권과 일일권의 optionKey 가 같은 숫자일 수 있어 유형을 붙여 구분한다
export const otTicketValue = ticket => `${ticket.ticketType}-${ticket.optionKey}`;

export const getLessonAttendStatus = event => {
  if ((event.attendedMemberCount || 0) > 0) return 'attended';
  if ((event.absentedMemberCount || 0) > 0) return 'absented';
  return 'reserved';
};

// 여러 명 OT 카드 상태 — 예약중이 하나라도 있으면 예약, 없고 출석이 있으면 출석, 결석만 있으면 결석.
// 대기·취소는 카운트에 없다. 예약 회원이 아무도 없으면 예약으로 본다.
const getOtGroupAttendStatus = event => {
  if ((event.reservedMemberCount || 0) > 0) return 'reserved';
  if ((event.attendedMemberCount || 0) > 0) return 'attended';
  if ((event.absentedMemberCount || 0) > 0) return 'absented';
  return 'reserved';
};

// 수업 유형 필터 값 — 헤더 세그먼트(전체/그룹/개인/OT/상담/기타)와 1:1
export const TYPE_FILTER_ALL = 'all';
export const TYPE_FILTER_GROUP = 'group';
export const TYPE_FILTER_PRIVATE = 'private';
export const TYPE_FILTER_OT = 'ot';
export const TYPE_FILTER_CONSULT = 'consult';
export const TYPE_FILTER_ETC = 'etc';

// 유형 필터 판정 — '개인'은 개인레슨 + 듀엣레슨을 함께 본다. OT 는 별도 유형.
export const matchesTypeFilter = (event, typeFilter) => {
  if (typeFilter === TYPE_FILTER_ALL) return true;
  if (typeFilter === TYPE_FILTER_GROUP) return event.courseClassType === 'GROUP_LESSON';
  if (typeFilter === TYPE_FILTER_PRIVATE)
    return event.courseClassType === 'PRIVATE_LESSON' || event.courseClassType === 'DUET_LESSON';
  if (typeFilter === TYPE_FILTER_OT) return event.courseClassType === 'OT';
  if (typeFilter === TYPE_FILTER_ETC) return event.courseClassType === 'ETC';
  return event.courseClassType === 'CONSULTATION';
};

/**
 * 조회 기간 요약 카운트 — 선택 유형만 집계한다.
 *
 * - scheduleCount: 그룹·상담까지 포함한 전체 일정 건수
 * - reserved/attended/absented: 개인·듀엣의 예약 상태별 인원수 (유형=OT 일 때는 OT 의 인원수)
 * - otCount: OT 건수 — OT 는 개인과 분리해 따로 센다
 *   (그룹은 한 수업에 여러 상태가 섞여 있어 이 칩에 합치지 않고 groupCount 로 따로 센다)
 * - groupCount / consultCount / etcCount: 상태 개념이 없는 유형의 건수
 */
export const summarizeEvents = (events, typeFilter = TYPE_FILTER_ALL) => {
  const acc = {
    scheduleCount: 0, groupCount: 0, otCount: 0, consultCount: 0, etcCount: 0, reserved: 0, attended: 0, absented: 0,
    // 그룹 예약 상태별 건수 (유형=그룹 칩용)
    groupFull: 0, groupUnder: 0, groupNone: 0,
    // 상담 결과별 건수 (유형=상담 칩용). 결과가 없는 과거 상담은 예정으로 센다.
    consultByResult: {
      SCHEDULED: 0, RESCHEDULED: 0, UNCONFIRMED: 0, NO_SHOW: 0, VISITED: 0,
      CONSIDERING: 0, NOT_REGISTERED: 0, NEW_REGISTERED: 0, RE_REGISTERED: 0,
    },
  };
  (events || []).forEach(event => {
    if (!matchesTypeFilter(event, typeFilter)) return;
    acc.scheduleCount += 1;
    if (event.courseClassType === 'CONSULTATION') {
      acc.consultCount += 1;
      const result = event.consultResult || 'SCHEDULED';
      if (acc.consultByResult[result] != null) acc.consultByResult[result] += 1;
      return;
    }
    if (event.courseClassType === 'ETC') {
      acc.etcCount += 1;
      return;
    }
    if (event.courseClassType === 'GROUP_LESSON') {
      acc.groupCount += 1;
      const groupStatus = getGroupReserveStatus(event);
      if (groupStatus === 'full') acc.groupFull += 1;
      else if (groupStatus === 'none') acc.groupNone += 1;
      else if (groupStatus === 'under') acc.groupUnder += 1;
      return;
    }
    if (event.courseClassType === 'OT') {
      acc.otCount += 1;
      // 예약/출석/결석 인원은 OT 를 단독으로 볼 때만 OT 로 채운다(전체에서는 개인·듀엣 전용)
      if (typeFilter !== TYPE_FILTER_OT) return;
    }
    acc.reserved += event.reservedMemberCount || 0;
    acc.attended += event.attendedMemberCount || 0;
    acc.absented += event.absentedMemberCount || 0;
  });
  return acc;
};

// 그룹레슨 예약 상태 — full(예약마감)/under(미달)/none(없음)/closed(폐강)
export const getGroupReserveStatus = event => {
  const reserved = event.reservedMemberCount || 0;
  const attended = event.attendedMemberCount || 0;
  const absented = event.absentedMemberCount || 0;
  const max = event.membersMaxCount || event.maxMemberCount || 0;
  if (event.openYn === 'N' || event.closeDate) return 'closed';
  if (max > 0 && reserved + attended >= max) return 'full';
  if (reserved === 0 && attended === 0 && absented === 0) return 'none';
  return 'under';
};

// 수강권 임박 = 잔여 세션 <= 지점 만료임박 기준(expiration-alerts의 ticketAlertCount).
// 단위가 PERCENT면 기준값을 총 세션 수 대비 올림 환산한다 (10회권 10% → 1회). 환산식은 백엔드
// AlertThresholdCalculator와 동일. 단 PERCENT인데 totalSessionCount가 없으면 판정하지 않는다
// (호출부가 세션정보 있는 이벤트에만 태우므로 실제로는 미도달 경로).
export const isTicketExpiring = (event, ticketAlertCount, ticketAlertCountUnit) => {
  if (typeof event.remainingSessionCount !== 'number' || ticketAlertCount == null) return false;
  let threshold = ticketAlertCount;
  if (ticketAlertCountUnit === 'PERCENT') {
    if (typeof event.totalSessionCount !== 'number') return false;
    threshold = Math.ceil((event.totalSessionCount * ticketAlertCount) / 100);
  }
  return event.remainingSessionCount <= threshold;
};

export const GROUP_LESSON_LABEL = '그룹레슨';

// 그룹레슨 인원 표기 'n/정원' — 정원이 없으면 null (이때는 클래스명으로 표기한다)
// 상세 패널은 이 값을 별도 span 으로 뽑아 살짝 올려 그리므로 라벨과 분리해서 제공한다.
export const getGroupMemberCount = event => {
  if (event.courseClassType !== 'GROUP_LESSON' || !event.membersMaxCount) return null;
  const totalMemberCount =
    (event.reservedMemberCount || 0) + (event.attendedMemberCount || 0) + (event.absentedMemberCount || 0);
  return `${totalMemberCount}/${event.membersMaxCount}`;
};

// 목록 표기명 — 그룹은 '그룹레슨 n/정원', 듀엣은 '김수현 외 1', 개인은 회원명 (월간 행·주/월 패널 공통)
// 괄호를 쓰지 않는 이유: Pretendard 의 '(' ')' 는 베이스라인 아래로 2.59px 내려가 한글(1.13px)보다
// 1.46px 더 처져 회원수만 아래로 밀려 보인다. 괄호를 빼면 차이가 0.25px 로 사라진다.
export const getEventDisplayName = event => {
  // 기타는 제목이 곧 내용이라 그대로 쓴다 (회원·정원 개념이 없다).
  if (event.courseClassType === 'ETC') return event.className || '';
  // 상담은 결과를 앞에 두고 이름을 뒤에 둔다. 좁아지면 뒤(이름)부터 …로 잘리므로 결과는 항상 읽힌다.
  if (event.courseClassType === 'CONSULTATION') {
    const resultLabel = getConsultResultLabel(event.consultResult || 'SCHEDULED');
    const name = event.memberName || '';
    return name ? `${resultLabel} · ${name}` : resultLabel;
  }
  if (event.courseClassType === 'GROUP_LESSON') {
    const memberCount = getGroupMemberCount(event);
    return memberCount ? `${GROUP_LESSON_LABEL} ${memberCount}` : event.className || '';
  }
  if ((event.courseClassType === 'DUET_LESSON' || event.courseClassType === 'OT') && event.members?.length > 0) {
    const first = event.members[0]?.memberName || '';
    return event.members.length > 1 ? `${first} 외 ${event.members.length - 1}` : first;
  }
  return event.memberName || event.className || '';
};

/**
 * 유형 단독 선택 시의 카드 색 메타 — 그룹은 예약 상태색, 상담은 결과색, OT 는 개인레슨과 같은 출석 상태색.
 * (1명 OT 는 회원 1명이라 수업 카운트가 곧 그 회원 상태, 여러 명 OT 는 getOtGroupAttendStatus)
 * 카운트 칩과 같은 색을 카드 배경에 쓰기 위한 것이라 전체·개인 유형에서는 null 을 돌려
 * 각 뷰가 기존 색(그룹=중성 회색, 상담=공통 하늘색)을 그대로 쓰게 한다.
 */
export const getFilteredStatusCardMeta = (event, typeFilter) => {
  if (typeFilter === TYPE_FILTER_GROUP && event.courseClassType === 'GROUP_LESSON') {
    return GROUP_STATUS_CARD_META[getGroupReserveStatus(event)] || null;
  }
  if (typeFilter === TYPE_FILTER_CONSULT && event.courseClassType === 'CONSULTATION') {
    return CONSULT_RESULT_CARD_META[event.consultResult] || CONSULT_RESULT_CARD_META.SCHEDULED;
  }
  if (typeFilter === TYPE_FILTER_OT && event.courseClassType === 'OT') {
    return ATTEND_STATUS_META[isOtGroupEvent(event) ? getOtGroupAttendStatus(event) : getLessonAttendStatus(event)];
  }
  return null;
};

// 상태 바 색 — 개인·듀엣은 출석 상태색, 그룹은 중성 회색, 상담은 유형색.
// 그룹·상담을 단독으로 보는 중이면 카운트 칩과 같은 상태색으로 칠한다.
export const getEventBarColor = (event, typeFilter = TYPE_FILTER_ALL) => {
  const filteredMeta = getFilteredStatusCardMeta(event, typeFilter);
  if (filteredMeta) return filteredMeta.barColor;
  if (isLessonEvent(event)) return ATTEND_STATUS_META[getLessonAttendStatus(event)].barColor;
  if (event.courseClassType === 'GROUP_LESSON') return GROUP_BAR_COLOR;
  return EVENT_TYPE_META[getEventTypeKey(event.courseClassType)].color;
};
